// src/pages/ManageEmployees.js
import { useState, useEffect } from "react";
import { useAuth } from "../Context/AuthContext";
import { db } from "../firebase/config";
import { getAuth } from "firebase/auth";
import {
  getDoc,
  doc,
  updateDoc,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import NoDataIllustration from "../tools/assets/undraw_no-data_ig65.svg";

import { useNavigate } from "react-router-dom";
import {
  addEmployee,
  getEmployees,
  removeEmployee,
} from "../firebase/employees";
import "../styles/ManageEmployee.css";
import { isPilotActive } from "../utils/pilot";
// import { getPortalUrl } from "../firebase/stripeFunctions";

const DEFAULT_GROUPS = ["IT", "Finance", "Sales", "Marketing", "Operations"];

// --- Visual badge for incomplete profiles ---
function IncompleteBadge({ title = "Profile incomplete" }) {
  return (
    <span
      className="badge-incomplete"
      title={title}
      aria-label={title}
      role="status"
    >
      <span className="badge-dot" aria-hidden="true" />
    </span>
  );
}

export default function ManageEmployees({ onEmployeeClick = () => {} }) {
  const { user, loading } = useAuth();
  const companyId = user?.companyId;
  const navigate = useNavigate();

  // ------- Existing state -------
  const [lastName, setLastName] = useState("");
  const [employees, setEmployees] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false); // 👈 NEW
  const [middleName, setMiddleName] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [companyPilot, setCompanyPilot] = useState(null);
  const [effectiveLimit, setEffectiveLimit] = useState(null);
  const [payRate, setPayRate] = useState(""); // 👈 NEW

  // Role is fixed to "employee" (no UI shown)
  const role = "employee";

  const [jobTitle, setJobTitle] = useState("");
  const [group, setGroup] = useState("");
  const [error, setError] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [requireOnboarding, setRequireOnboarding] = useState(true);
  const [employeeLimit, setEmployeeLimit] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  // const [isUpgrading, setIsUpgrading] = useState(false);

  const [helpdeskRole, setHelpdeskRole] = useState(""); // "" | "agent" | "manager"
  const [confirmTarget, setConfirmTarget] = useState(null); // {id, name, email} | null
  const [isRemoving, setIsRemoving] = useState(false);

  // ------- Group customization (with Firestore persistence) -------
  const [groupOptions, setGroupOptions] = useState([...DEFAULT_GROUPS]);
  const [groupMode, setGroupMode] = useState("select"); // "select" | "custom"
  const [customGroup, setCustomGroup] = useState("");

  // ------- Employment lifecycle -------
  const [employmentType, setEmploymentType] = useState("full_time"); // "full_time" | "part_time"
  const [startDate, setStartDate] = useState(""); // replaces hireDate in UI
  const [endDate, setEndDate] = useState("");
  const [noEndDate, setNoEndDate] = useState(false);

  const seedFakeEmployees = async () => {
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (!currentUser) {
        alert("Not logged in");
        return;
      }

      const idToken = await currentUser.getIdToken();

      // Replace YOUR_REGION and YOUR_PROJECT with actual values
      const url =
        "https://us-central1-employee-b47f3.cloudfunctions.net/seedTestEmployees";

      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          companyId,
          count: 10, // how many fake employees you want
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        console.error("Seed error:", data);
        alert("Seed failed: " + (data.error || "Unknown error"));
        return;
      }

      console.log("Seed result:", data);
      alert(`Created ${data.created} fake employees.`);
      await refreshEmployees(); // you already defined this
    } catch (e) {
      console.error(e);
      alert("Error seeding employees: " + e.message);
    }
  };

  // Reload employees list from Firestore (used by bulk add)
  const refreshEmployees = async () => {
    if (!companyId) return;
    try {
      const updated = await getEmployees(companyId);
      setEmployees(updated);
    } catch (err) {
      console.error("Failed to refresh employees:", err.message);
    }
  };

  // Load employees + company data (limit, plan, saved groups)
  useEffect(() => {
    if (!loading && companyId) {
      (async () => {
        try {
          const employeesList = await getEmployees(companyId);
          setEmployees(employeesList);

          const companyRef = doc(db, "companies", companyId);
          const companySnap = await getDoc(companyRef);

          if (companySnap.exists()) {
            const companyData = companySnap.data();

            const planLimitRaw = Number(companyData.employeeLimit);
            const planLimit = Number.isFinite(planLimitRaw) ? planLimitRaw : 10;

            const pilotObj = companyData.pilot || null;
            setCompanyPilot(pilotObj);

            // pilot limit only matters if pilot is active
            const pilotLimitRaw = Number(pilotObj?.employeeLimit);
            const pilotLimit = Number.isFinite(pilotLimitRaw)
              ? pilotLimitRaw
              : null;

            const pilotActive = isPilotActive(pilotObj);

            setEmployeeLimit(planLimit);
            setEffectiveLimit(
              pilotActive && pilotLimit ? pilotLimit : planLimit,
            );

            // Merge saved groups from Firestore (skip defaults)
            if (Array.isArray(companyData.groups)) {
              const customs = companyData.groups.filter(
                (g) => !DEFAULT_GROUPS.includes(g),
              );
              setGroupOptions([...DEFAULT_GROUPS, ...customs]);
            }
          }
        } catch (err) {
          console.error(
            "Failed to fetch employees or company info:",
            err.message,
          );
          setError("Error loading employees or company data.");
        }
      })();
    }
  }, [loading, companyId]);

  // Persist a new custom group to Firestore and local state
  const addCustomGroupToCompany = async (label) => {
    const val = (label || "").trim();
    if (!val) return;

    // prevent dupes (case-insensitive)
    const exists = groupOptions.some(
      (x) => x.toLowerCase() === val.toLowerCase(),
    );

    if (exists) {
      setGroupOptions((prev) => {
        const found = prev.find((x) => x.toLowerCase() === val.toLowerCase());
        if (found) setGroup(found); // use canonical casing
        return prev;
      });
      setCustomGroup("");
      setGroupMode("select");
      return;
    }

    // don't write defaults
    if (DEFAULT_GROUPS.includes(val)) {
      setGroup(val);
      setCustomGroup("");
      setGroupMode("select");
      return;
    }

    try {
      await updateDoc(doc(db, "companies", companyId), {
        groups: arrayUnion(val),
      });
      setGroupOptions((prev) => [...prev, val]);
      setGroup(val);
      setCustomGroup("");
      setGroupMode("select");
    } catch (e) {
      console.error("Failed to add custom group:", e);
      setError("Could not save custom group. Try again.");
    }
  };

  // Remove a custom group from Firestore and local state
  const deleteCustomGroupFromCompany = async (label) => {
    try {
      await updateDoc(doc(db, "companies", companyId), {
        groups: arrayRemove(label),
      });
      setGroupOptions((prev) => prev.filter((g) => g !== label));
      if (group === label) setGroup("");
    } catch (e) {
      console.error("Failed to delete custom group:", e);
      setError("Could not delete custom group. Try again.");
    }
  };

  // Add employee submit
  const handleAddEmployee = async (e) => {
    e.preventDefault();
    setError("");

    if (!companyId) {
      setError("Company ID is missing.");
      return;
    }

    if (effectiveLimit !== null && employees.length >= effectiveLimit) {
      setShowUpgradeModal(true);
      return;
    }

    // normalize group selection to decide if we need a role
    const normalizedSelection = (group || customGroup || "").toString().trim();
    const isITSelected = normalizedSelection.toUpperCase() === "IT";
    if (isITSelected && !helpdeskRole) {
      setError("Please choose a Help Desk role.");
      return;
    }

    // lifecycle validation
    if (!startDate) {
      setError("Start date is required.");
      return;
    }

    if (employmentType === "part_time" && !noEndDate) {
      if (!endDate) {
        setError("Please provide an end date or mark as ongoing.");
        return;
      }
      if (new Date(endDate) < new Date(startDate)) {
        setError("End date cannot be earlier than start date.");
        return;
      }
    }

    const normalizedEmail = String(email || "")
      .trim()
      .toLowerCase();

    // canonicalize final group; keep role iff IT
    const rawGroup = normalizedSelection;
    const finalGroup = rawGroup.toUpperCase() === "IT" ? "IT" : rawGroup;
    const isITFinal = finalGroup.toUpperCase() === "IT";

    // pay rate validation (optional)
    let payRateValue = null;
    if (payRate !== "" && payRate != null) {
      const n = Number(payRate);
      if (Number.isNaN(n) || n < 0) {
        setError("Enter a valid non-negative hourly rate.");
        return;
      }
      payRateValue = n;
    }

    setIsSubmitting(true);
    try {
      await addEmployee(companyId, {
        name,
        lastName,
        middleName,
        email: normalizedEmail, // ✅ normalized
        jobTitle,
        payRate: payRateValue,
        role,
        group: finalGroup,
        helpdeskRole: isITFinal ? helpdeskRole : "",
        employmentType,
        startDate,
        endDate: noEndDate ? null : endDate || null,
        ongoing: employmentType === "part_time" ? Boolean(noEndDate) : true,
        hireDate: startDate, // legacy mapping

        // Always start incomplete; who completes depends on the toggle
        profileCompleted: false,
        selfOnboarding: requireOnboarding,

        status: "Pending", // optional but handy for filters
      });

      const updatedEmployees = await getEmployees(companyId);
      setEmployees(updatedEmployees);

      // Reset form
      setName("");
      setLastName("");
      setMiddleName("");
      setEmail("");
      setJobTitle("");
      setPayRate("");
      setGroup("");
      setHelpdeskRole("");
      setError("");
      setShowModal(false);

      setEmploymentType("full_time");
      setStartDate("");
      setEndDate("");
      setNoEndDate(false);

      setGroupMode("select");
      setCustomGroup("");
    } catch (err) {
      console.error("Add employee error:", err.message);
      setError("Failed to add employee.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Remove employee
  const handleRemoveEmployee = async (id) => {
    try {
      await removeEmployee(companyId, id);
      setEmployees((prev) => prev.filter((emp) => emp.id !== id));
    } catch (err) {
      console.error("Remove employee error:", err.message);
      setError("Failed to remove employee.");
    }
  };

  // For the ⚠️ badge (legacy list)
  const isProfileIncomplete = (employee) => {
    const requiredFields = [
      "preferredFirstName",
      "phone",
      "gender",
      "address",
      "birthDate",
      "role",
      "jobTitle",
      "group",
      "hireDate",
    ];
    return requiredFields.some((field) => !employee[field]);
  };

  const isPilotCompany = isPilotActive(companyPilot);

  if (loading) return <p>Loading user data...</p>;
  if (!user) return <p style={{ color: "red" }}>User not authenticated.</p>;

  return (
    <div>
      <div className="employee-management-container">
        <div className="add-employee-container">
          <div
            style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}
          >
            <button
              className="add-btn"
              onClick={() => {
                if (
                  effectiveLimit !== null &&
                  employees.length >= effectiveLimit
                ) {
                  setShowUpgradeModal(true);
                } else {
                  setShowModal(true);
                }
              }}
            >
              Add Employee
            </button>

            <button
              className="add-btn1 add-btn-secondary"
              type="button"
              onClick={() => {
                if (
                  employeeLimit !== null &&
                  employees.length >= employeeLimit
                ) {
                  setShowUpgradeModal(true);
                } else {
                  setShowBulkModal(true);
                }
              }}
            >
              Bulk add employees
            </button>

            {false && (
              <button
                type="button"
                className="add-btn1 add-btn-secondary"
                onClick={seedFakeEmployees}
                style={{ marginLeft: "8px" }}
              >
                DEV: Seed 40 Fake Employees
              </button>
            )}
          </div>
        </div>

        <div className="status-filter-wrapper">
          <select
            className="status-dropdown"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
          >
            <option value="All">All Employees</option>
            <option value="Active">Active Employees</option>
            <option value="Pending">Pending Employees</option>
            <option value="Terminated">Terminated Employees</option>
          </select>

          <div className="search-wrapper">
            <input
              type="text"
              className="search-input-with-icon"
              placeholder="Search by name or email"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {error && <p className="error">{error}</p>}

        <div className="employee-card-table">
          {/* Headers */}
          <div className="employee-card-header">
            <div className="employee-name-section">
              <strong>Name</strong>
            </div>
            <div className="employee-job">
              <strong>Job Title</strong>
            </div>
            <div className="employee-email">
              <strong>Email</strong>
            </div>
            <div className="employee-action">
              <strong>Actions</strong>
            </div>
          </div>

          {/* Rows */}
          {employees.length === 0 ? (
            <div className="empty-state">
              <img
                src={NoDataIllustration}
                alt="No employees yet"
                className="empty-state__img"
              />

              <h3 className="empty-state__title">
                You haven’t added any employees yet
              </h3>

              <p className="empty-state__text">
                Add your first employee to start scheduling shifts, tracking
                time, and managing your team.
              </p>

              <button className="add-btn" onClick={() => setShowModal(true)}>
                Add your first employee
              </button>
            </div>
          ) : (
            employees
              .filter((emp) =>
                selectedStatus === "All" ? true : emp.status === selectedStatus,
              )
              .filter((emp) => {
                const term = searchTerm.toLowerCase();
                return (
                  emp.name?.toLowerCase().includes(term) ||
                  emp.email?.toLowerCase().includes(term)
                );
              })
              .map((emp) => (
                <div key={emp.id} className="employee-list-card">
                  <div
                    className="employee-card-row"
                    onClick={() => {
                      onEmployeeClick(emp);
                      navigate(`/employee/${emp.id}`);
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) =>
                      e.key === "Enter" && navigate(`/employee/${emp.id}`)
                    }
                  >
                    <div className="employee-name-section">
                      <div className="avatar-circle">
                        {emp.name?.charAt(0).toUpperCase() || "U"}
                      </div>

                      <span className="employee-name">
                        {emp.name ?? "Unnamed"}{" "}
                        {emp && isProfileIncomplete(emp) && <IncompleteBadge />}
                      </span>
                    </div>

                    <div className="employee-job">{emp.jobTitle}</div>
                    <div className="employee-email">{emp.email}</div>

                    <div className="employee-action">
                      <button
                        type="button"
                        className="remove-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setConfirmTarget({
                            id: emp.id,
                            name: emp.name,
                            email: emp.email,
                          });
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))
          )}
        </div>

        {/* Modal: Add Employee */}
        {showModal && (
          <div
            className="modal-overlay"
            onClick={(e) => {
              if (e.target === e.currentTarget) setShowModal(false);
            }}
          >
            <div
              className="me-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="me-modal-title"
            >
              <div className="me-modal__header">
                <h3 id="me-modal-title">Add an employee</h3>
                <button
                  type="button"
                  className="me-close"
                  aria-label="Close"
                  onClick={() => setShowModal(false)}
                >
                  ×
                </button>
              </div>

              <div className="me-modal__body">
                <form onSubmit={handleAddEmployee} className="me-form">
                  <div className="me-grid">
                    <label className="me-field">
                      <span>First Name</span>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                      />
                    </label>

                    <label className="me-field">
                      <span>M.I</span>
                      <input
                        type="text"
                        value={middleName}
                        onChange={(e) => setMiddleName(e.target.value)}
                      />
                    </label>

                    <label className="me-field">
                      <span>Last Name</span>
                      <input
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        required
                      />
                    </label>

                    {/* Employment Type */}
                    <label className="me-field">
                      <span>Employment Type</span>
                      <select
                        value={employmentType}
                        onChange={(e) => {
                          const v = e.target.value;
                          setEmploymentType(v);
                          if (v === "full_time") {
                            setEndDate("");
                            setNoEndDate(false);
                          }
                        }}
                        required
                      >
                        <option value="full_time">Full-time</option>
                        <option value="part_time">Part-time / Contract</option>
                      </select>
                    </label>

                    {/* Start Date */}
                    <label className="me-field">
                      <span>Start Date</span>
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        required
                      />
                    </label>

                    {/* End Date for PT/contract */}
                    {employmentType === "part_time" && (
                      <>
                        <label className="me-field">
                          <span>End Date</span>
                          <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            disabled={noEndDate}
                            required={!noEndDate}
                          />
                        </label>

                        <div className="me-field" style={{ alignSelf: "end" }}>
                          <label
                            style={{
                              display: "inline-flex",
                              gap: 8,
                              alignItems: "center",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={noEndDate}
                              onChange={(e) => {
                                setNoEndDate(e.target.checked);
                                if (e.target.checked) setEndDate("");
                              }}
                            />
                            <span>No end date (ongoing)</span>
                          </label>
                        </div>
                      </>
                    )}

                    <label className="me-field me-col-2">
                      <span>Email</span>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </label>

                    <label className="me-field me-col-2">
                      <span>Job Title</span>
                      <input
                        type="text"
                        value={jobTitle}
                        onChange={(e) => setJobTitle(e.target.value)}
                        required
                      />
                    </label>

                    <label className="me-field">
                      <span>Pay Rate (per hour)</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={payRate}
                        onChange={(e) => setPayRate(e.target.value)}
                        placeholder="e.g. 20.00"
                      />
                    </label>

                    {/* Group / Department */}
                    <label className="me-field me-col-2">
                      <span>Group / Department</span>

                      {groupMode === "select" ? (
                        <select
                          value={group}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v === "__custom__") {
                              setGroupMode("custom");
                              setGroup("");
                              return;
                            }
                            setGroup(v);
                          }}
                          required
                        >
                          <option value="">Select Group</option>
                          {groupOptions.map((g) => (
                            <option key={g} value={g}>
                              {g}
                            </option>
                          ))}
                          <option value="__custom__">+ Add custom…</option>
                        </select>
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "1rem",
                          }}
                        >
                          <div style={{ display: "flex", gap: 8 }}>
                            <input
                              type="text"
                              placeholder="Enter a new group (e.g., Customer Success)"
                              value={customGroup}
                              onChange={(e) => setCustomGroup(e.target.value)}
                              required
                            />
                            <button
                              type="button"
                              className="me-btn"
                              onClick={() =>
                                addCustomGroupToCompany(customGroup)
                              }
                            >
                              Add
                            </button>
                            <button
                              type="button"
                              className="me-btn me-btn--ghost"
                              onClick={() => {
                                setCustomGroup("");
                                setGroupMode("select");
                                setGroup("");
                              }}
                            >
                              Cancel
                            </button>
                          </div>

                          {/* Existing custom groups with delete buttons */}
                          <div>
                            <p
                              style={{
                                fontWeight: 500,
                                marginBottom: "0.3rem",
                              }}
                            >
                              Existing Custom Groups:
                            </p>
                            {groupOptions
                              .filter((g) => !DEFAULT_GROUPS.includes(g))
                              .map((g) => (
                                <div
                                  key={g}
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: 4,
                                    background: "#f8f9fa",
                                    padding: "4px 8px",
                                    borderRadius: 4,
                                  }}
                                >
                                  <span>{g}</span>
                                  <button
                                    type="button"
                                    style={{
                                      background: "transparent",
                                      border: "none",
                                      color: "#d9534f",
                                      cursor: "pointer",
                                      fontSize: "0.9rem",
                                    }}
                                    onClick={() =>
                                      deleteCustomGroupFromCompany(g)
                                    }
                                  >
                                    ✕
                                  </button>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}
                    </label>

                    {/* Help Desk Role (only if IT group, case-insensitive) */}
                    {(group || customGroup || "")
                      .toString()
                      .trim()
                      .toUpperCase() === "IT" && (
                      <label className="me-field">
                        <span>Help Desk Role</span>
                        <select
                          value={helpdeskRole}
                          onChange={(e) => setHelpdeskRole(e.target.value)}
                          required
                        >
                          <option value="">Select</option>
                          <option value="agent">Helpdesk Agent</option>
                          <option value="manager">Helpdesk Manager</option>
                        </select>
                      </label>
                    )}
                  </div>

                  {/* Toggles */}
                  <div className="me-section">
                    <div className="me-toggle-row">
                      <span>Require employee to complete onboarding?</span>
                      <label className="me-switch">
                        <input
                          type="checkbox"
                          checked={requireOnboarding}
                          onChange={(e) =>
                            setRequireOnboarding(e.target.checked)
                          }
                        />
                        <span className="me-slider"></span>
                      </label>
                    </div>

                    <p className="me-onboarding-disclaimer">
                      The employee will receive an email with a secure link to
                      sign in and access their dashboard.
                      {requireOnboarding ? (
                        <>
                          {" "}
                          They’ll also be prompted to complete their profile
                          during first login.
                        </>
                      ) : (
                        <>
                          {" "}
                          You’ll complete their profile from the Employee
                          Details page.
                        </>
                      )}
                    </p>
                  </div>

                  {/* Footer */}
                  {error && <p className="me-error">{error}</p>}

                  <div className="me-modal__footer">
                    <button
                      className="me-btn me-btn--primary"
                      type="submit"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Adding…" : "Add an Employee"}
                    </button>
                    <button
                      type="button"
                      className="me-btn me-btn--ghost"
                      onClick={() => setShowModal(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Modal: Bulk Add Employees */}
        {showBulkModal && (
          <BulkAddEmployeesModal
            companyId={companyId}
            groupOptions={groupOptions}
            employeeLimit={effectiveLimit}
            existingCount={employees.length}
            onClose={() => setShowBulkModal(false)}
            onEmployeesAdded={async () => {
              await refreshEmployees();
              setShowBulkModal(false);
            }}
            addCustomGroupToCompany={addCustomGroupToCompany}
            deleteCustomGroupFromCompany={deleteCustomGroupFromCompany}
          />
        )}

        {/* Remove confirmation modal */}
        {confirmTarget && (
          <div
            className="modal-overlay"
            onClick={(e) => {
              if (e.target === e.currentTarget) setConfirmTarget(null);
            }}
          >
            <div
              className="modal small"
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="remove-emp-title"
            >
              <h3 id="remove-emp-title">Remove employee?</h3>
              <p className="warn-text">
                You’re about to remove{" "}
                <strong>{confirmTarget.name || "this user"}</strong>
                {confirmTarget.email ? <> ({confirmTarget.email})</> : null}.
                This will revoke their access and remove them from your roster.
                This action can’t be undone.
              </p>

              <div className="modal-buttons">
                <button
                  className="danger-btn"
                  onClick={async () => {
                    setIsRemoving(true);
                    try {
                      await handleRemoveEmployee(confirmTarget.id);
                    } finally {
                      setIsRemoving(false);
                      setConfirmTarget(null);
                    }
                  }}
                  disabled={isRemoving}
                >
                  {isRemoving ? "Removing…" : "Yes, remove"}
                </button>

                <button
                  className="cancel-btn"
                  type="button"
                  onClick={() => setConfirmTarget(null)}
                  disabled={isRemoving}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Upgrade / Pilot limit modal */}
        {effectiveLimit !== null &&
          employees.length >= effectiveLimit &&
          showUpgradeModal && (
            <div className="modal-overlay">
              <div className="modal">
                <h3>
                  {isPilotCompany
                    ? "Pilot Employee Limit Reached"
                    : "Employee Limit Reached"}
                </h3>

                <p
                  style={{ fontSize: "1rem", color: "#444", lineHeight: "1.6" }}
                >
                  {isPilotCompany ? (
                    <>
                      Your pilot access allows a maximum of{" "}
                      <strong>{effectiveLimit}</strong> employees.
                      <br />
                      To add more, please subscribe to a plan after the pilot
                      ends or contact support if you need an adjustment.
                    </>
                  ) : (
                    <>
                      Your current plan allows a maximum of{" "}
                      <strong>{effectiveLimit}</strong> employees.
                      <br />
                      To add more, please upgrade your subscription.
                    </>
                  )}
                </p>

                <div className="modal-buttons" style={{ marginTop: "2rem" }}>
                  {!isPilotCompany && (
                    <button
                      className="general-savebuttons"
                      onClick={() => {
                        setShowUpgradeModal(false);
                        navigate("/subscription", {
                          state: { intent: "upgrade", source: "employeeCap" },
                        });
                      }}
                    >
                      Change Plan in App
                    </button>
                  )}

                  <button
                    className="general-cancelbuttons"
                    onClick={() => setShowUpgradeModal(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
      </div>
    </div>
  );
}

function BulkAddEmployeesModal({
  companyId,
  groupOptions,
  employeeLimit,
  existingCount,
  onClose,
  onEmployeesAdded,
  addCustomGroupToCompany, // 👈 new
  deleteCustomGroupFromCompany, // 👈 new
}) {
  const makeEmptyRow = () => ({
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    jobTitle: "",
    payRate: "",
    employmentType: "full_time", // full_time | part_time
    startDate: "",
    endDate: "",
    group: "",
    helpdeskRole: "",
    requireOnboarding: true,
  });

  const [rows, setRows] = useState([makeEmptyRow(), makeEmptyRow()]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [bulkGroupMode, setBulkGroupMode] = useState("select"); // "select" | "custom"
  const [bulkCustomGroup, setBulkCustomGroup] = useState("");

  const handleChange = (index, field, value) => {
    setRows((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };

      // if group changed away from IT, clear helpdeskRole
      if (field === "group") {
        const g = (value || "")
          .toString()
          .trim()
          .toUpperCase();
        if (g !== "IT") copy[index].helpdeskRole = "";
      }
      return copy;
    });
  };

  const addRow = () => setRows((prev) => [...prev, makeEmptyRow()]);

  const removeRow = (idx) => {
    setRows((prev) => {
      if (prev.length === 1) return prev;
      return prev.filter((_, i) => i !== idx);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Only keep rows that have at least a name or email filled
    const activeRows = rows.filter((r) =>
      [r.firstName, r.lastName, r.email, r.jobTitle].some((v) =>
        String(v || "").trim(),
      ),
    );

    if (!activeRows.length) {
      setError("Please fill at least one row before adding employees.");
      return;
    }

    // Plan limit check
    if (
      employeeLimit !== null &&
      existingCount + activeRows.length > employeeLimit
    ) {
      const available = Math.max(employeeLimit - existingCount, 0);
      setError(
        available > 0
          ? `Your plan allows only ${available} more employee${
              available === 1 ? "" : "s"
            }. Please remove some rows.`
          : "Your employee limit has been reached. Upgrade your plan to add more employees.",
      );
      return;
    }

    // Basic validation
    for (let i = 0; i < activeRows.length; i++) {
      const row = activeRows[i];
      const rowNum = i + 1;

      // pay rate validation (optional)
      if (row.payRate !== "" && row.payRate != null) {
        const n = Number(row.payRate);
        if (Number.isNaN(n) || n < 0) {
          setError(`Row ${rowNum}: Enter a valid non-negative pay rate.`);
          return;
        }
      }

      if (!row.firstName.trim())
        return setError(`Row ${rowNum}: First Name is required.`);
      if (!row.lastName.trim())
        return setError(`Row ${rowNum}: Last Name is required.`);
      if (!row.email.trim())
        return setError(`Row ${rowNum}: Email is required.`);
      if (!row.jobTitle.trim())
        return setError(`Row ${rowNum}: Job Title is required.`);
      if (!row.group.trim())
        return setError(`Row ${rowNum}: Group / Department is required.`);
      if (!row.startDate)
        return setError(`Row ${rowNum}: Start Date is required.`);

      if (row.employmentType === "part_time" && !row.endDate) {
        return setError(`Row ${rowNum}: End Date is required for part-time.`);
      }

      const isIT =
        (row.group || "")
          .toString()
          .trim()
          .toUpperCase() === "IT";
      if (isIT && !row.helpdeskRole) {
        return setError(
          `Row ${rowNum}: Help Desk role is required for IT employees.`,
        );
      }
    }

    setIsSubmitting(true);
    try {
      for (const row of activeRows) {
        const isIT =
          (row.group || "")
            .toString()
            .trim()
            .toUpperCase() === "IT";
        const employmentType = row.employmentType || "full_time";
        const normalizedEmail = String(row.email || "")
          .trim()
          .toLowerCase();

        await addEmployee(companyId, {
          name: row.firstName,
          middleName: row.middleName,
          lastName: row.lastName,
          email: normalizedEmail, // ✅ normalized
          jobTitle: row.jobTitle,

          payRate:
            row.payRate !== "" &&
            row.payRate != null &&
            !Number.isNaN(Number(row.payRate))
              ? Number(row.payRate)
              : null,

          role: "employee",
          group: row.group,
          helpdeskRole: isIT ? row.helpdeskRole : "",
          hireDate: row.startDate,

          employmentType,
          startDate: row.startDate,
          endDate: employmentType === "part_time" ? row.endDate : null,
          ongoing: employmentType === "full_time" ? true : false,

          profileCompleted: false,
          selfOnboarding: row.requireOnboarding,
        });
      }

      if (onEmployeesAdded) await onEmployeesAdded();
    } catch (err) {
      console.error("Bulk add error:", err.message);
      setError("Failed to add one or more employees. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isSubmitting) onClose();
      }}
    >
      <div
        className="me-modal me-modal--wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulk-modal-title"
      >
        <div className="me-modal__header">
          <h3 id="bulk-modal-title">Bulk Add Employees</h3>
          <button
            type="button"
            className="me-close"
            aria-label="Close"
            onClick={onClose}
            disabled={isSubmitting}
          >
            ×
          </button>
        </div>

        <div className="me-modal__body">
          <p
            style={{
              marginBottom: "0.75rem",
              fontSize: "0.9rem",
              color: "#555",
            }}
          >
            Employees will receive a secure sign-in link after they’re added. If
            “Self Onboarding” is enabled, they’ll complete their profile during
            first login.
          </p>
          <p
            style={{
              marginBottom: "0.75rem",
              fontSize: "0.9rem",
              color: "#555",
            }}
          >
            Fill in one row per employee. Blank rows will be ignored.
          </p>

          <form onSubmit={handleSubmit}>
            <div className="bulk-table-wrapper">
              <table className="bulk-table">
                <thead>
                  <tr>
                    <th>First Name</th>
                    <th>M.I</th>
                    <th>Last Name</th>
                    <th>Email</th>
                    <th>Job Title</th>
                    <th>Pay Rate</th>
                    <th>Type</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Group</th>
                    <th>Help Desk Role</th>
                    <th>Self Onboarding</th>
                    <th></th>
                  </tr>
                </thead>

                <tbody>
                  {rows.map((row, idx) => {
                    const isIT =
                      (row.group || "")
                        .toString()
                        .trim()
                        .toUpperCase() === "IT";

                    return (
                      <tr key={idx}>
                        <td>
                          <input
                            type="text"
                            value={row.firstName}
                            onChange={(e) =>
                              handleChange(idx, "firstName", e.target.value)
                            }
                          />
                        </td>

                        <td>
                          <input
                            type="text"
                            value={row.middleName}
                            onChange={(e) =>
                              handleChange(idx, "middleName", e.target.value)
                            }
                          />
                        </td>

                        <td>
                          <input
                            type="text"
                            value={row.lastName}
                            onChange={(e) =>
                              handleChange(idx, "lastName", e.target.value)
                            }
                          />
                        </td>

                        <td>
                          <input
                            type="email"
                            value={row.email}
                            onChange={(e) =>
                              handleChange(idx, "email", e.target.value)
                            }
                          />
                        </td>

                        <td>
                          <input
                            type="text"
                            value={row.jobTitle}
                            onChange={(e) =>
                              handleChange(idx, "jobTitle", e.target.value)
                            }
                          />
                        </td>

                        <td>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={row.payRate}
                            onChange={(e) =>
                              handleChange(idx, "payRate", e.target.value)
                            }
                            placeholder="20.00"
                          />
                        </td>

                        <td>
                          <select
                            value={row.employmentType}
                            onChange={(e) =>
                              handleChange(
                                idx,
                                "employmentType",
                                e.target.value,
                              )
                            }
                          >
                            <option value="full_time">Full-time</option>
                            <option value="part_time">
                              Part-time / Contract
                            </option>
                          </select>
                        </td>

                        <td>
                          <input
                            type="date"
                            value={row.startDate}
                            onChange={(e) =>
                              handleChange(idx, "startDate", e.target.value)
                            }
                          />
                        </td>

                        <td>
                          <input
                            type="date"
                            value={row.endDate}
                            onChange={(e) =>
                              handleChange(idx, "endDate", e.target.value)
                            }
                            disabled={row.employmentType === "full_time"}
                          />
                        </td>

                        <td>
                          {bulkGroupMode === "select" ? (
                            <select
                              value={row.group}
                              onChange={(e) => {
                                const v = e.target.value;
                                if (v === "__custom__") {
                                  setBulkGroupMode("custom");
                                  setBulkCustomGroup("");
                                  return;
                                }
                                handleChange(idx, "group", v);
                              }}
                            >
                              <option value="">Select</option>
                              {groupOptions.map((g) => (
                                <option key={g} value={g}>
                                  {g}
                                </option>
                              ))}
                              <option value="__custom__">+ Add custom…</option>
                            </select>
                          ) : (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 8,
                              }}
                            >
                              <div style={{ display: "flex", gap: 6 }}>
                                <input
                                  type="text"
                                  placeholder="New group name"
                                  value={bulkCustomGroup}
                                  onChange={(e) =>
                                    setBulkCustomGroup(e.target.value)
                                  }
                                  style={{ width: "120px" }}
                                />
                                <button
                                  type="button"
                                  className="me-btn"
                                  onClick={async () => {
                                    await addCustomGroupToCompany(
                                      bulkCustomGroup,
                                    );
                                    setBulkGroupMode("select");
                                    handleChange(idx, "group", bulkCustomGroup);
                                    setBulkCustomGroup("");
                                  }}
                                >
                                  Add
                                </button>
                                <button
                                  type="button"
                                  className="me-btn me-btn--ghost"
                                  onClick={() => {
                                    setBulkGroupMode("select");
                                    setBulkCustomGroup("");
                                  }}
                                >
                                  Cancel
                                </button>
                              </div>

                              {groupOptions
                                .filter((g) => !DEFAULT_GROUPS.includes(g))
                                .map((g) => (
                                  <div
                                    key={g}
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      background: "#f8f9fa",
                                      padding: "4px 6px",
                                      borderRadius: 4,
                                    }}
                                  >
                                    <span>{g}</span>
                                    <button
                                      type="button"
                                      style={{
                                        background: "transparent",
                                        border: "none",
                                        color: "#d9534f",
                                        cursor: "pointer",
                                      }}
                                      onClick={() =>
                                        deleteCustomGroupFromCompany(g)
                                      }
                                    >
                                      ✕
                                    </button>
                                  </div>
                                ))}
                            </div>
                          )}
                        </td>

                        <td>
                          {isIT ? (
                            <select
                              value={row.helpdeskRole}
                              onChange={(e) =>
                                handleChange(
                                  idx,
                                  "helpdeskRole",
                                  e.target.value,
                                )
                              }
                            >
                              <option value="">Select</option>
                              <option value="agent">Helpdesk Agent</option>
                              <option value="manager">Helpdesk Manager</option>
                            </select>
                          ) : (
                            <span style={{ fontSize: "0.8rem", color: "#888" }}>
                              —
                            </span>
                          )}
                        </td>

                        <td>
                          <label className="me-switch small">
                            <input
                              type="checkbox"
                              checked={row.requireOnboarding}
                              onChange={(e) =>
                                handleChange(
                                  idx,
                                  "requireOnboarding",
                                  e.target.checked,
                                )
                              }
                            />
                            <span className="me-slider"></span>
                          </label>
                        </td>

                        <td>
                          <button
                            type="button"
                            className="me-row-remove"
                            onClick={() => removeRow(idx)}
                            disabled={rows.length === 1}
                          >
                            Delete Row
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <button
              type="button"
              className="me-btn me-btn--ghost"
              style={{ marginTop: "0.75rem" }}
              onClick={addRow}
            >
              + Add Row
            </button>

            {error && (
              <p className="me-error" style={{ marginTop: 8 }}>
                {error}
              </p>
            )}

            <div className="me-modal__footer">
              <button
                className="me-btn me-btn--primary"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Adding…" : "Add Employees"}
              </button>
              <button
                type="button"
                className="me-btn me-btn--ghost"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
